/**
 * StageTable: the ten stages and their jobs as one table (Settings >
 * Pipeline). A stage row is one line: its number and name, whether it
 * runs for this team, the detail that says why, and the page that
 * changes it. Under a stage sit its jobs, the links of the pipeline
 * chain (TRA-1354), with the same columns on every row: what waits
 * against the threshold, the last run, the threshold itself, and "Run
 * now", the one thing on the page that writes. The last answer of each
 * "Run now" sits under its own row until the next click on that row.
 */

import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import type {
  TrainLoopLink as PipelineLink,
  TrainLoopLinkFired,
  TrainLoopLinkProgress,
  TrainLoopStage,
  TrainLoopStatus,
} from '@/api/generated/model'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Input } from '@/components/ui/input'
import { useTeam } from '@/contexts/TeamContext'
import { useRunTrainLoopLink, useSetTrainLoopLinkThreshold } from '@/hooks/useTeamSettings'
import { isNavRouteVisible } from '@/lib/nav'
import { cn } from '@/lib/utils'

import {
  STATUS_LABELS,
  THRESHOLD_MAX,
  THRESHOLD_MIN,
  TRAIN_LOOP_NODES,
  describeStartsAfter,
  firedText,
  lastRunText,
  linkState,
  progressText,
  thresholdError,
  type TrainLoopLink,
  type TrainLoopNode,
} from './trainLoop'

interface RowAnswer {
  fired: TrainLoopLinkFired | null
  error: unknown
}

const NO_ANSWER: RowAnswer = { fired: null, error: null }

const CELL = 'px-2 py-2 align-top'

export function StageTable({
  status,
  links,
}: {
  status: TrainLoopStatus
  links: PipelineLink[]
}): React.JSX.Element {
  const jobNames = new Map(links.map((link) => [link.slug, link.display_name]))
  const runMutation = useRunTrainLoopLink()
  const thresholdMutation = useSetTrainLoopLinkThreshold()
  const [savingSlug, setSavingSlug] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Record<string, RowAnswer>>({})
  const [pendingSlug, setPendingSlug] = useState<string | null>(null)
  const [asked, setAsked] = useState<PipelineLink | null>(null)
  const [isAsking, setIsAsking] = useState(false)

  const run = (slug: string): void => {
    setPendingSlug(slug)
    setAnswers((prev) => ({ ...prev, [slug]: NO_ANSWER }))
    runMutation.mutate(
      { slug },
      {
        onSuccess: (response) => {
          if (response.status === 200) {
            setAnswers((prev) => ({ ...prev, [slug]: { fired: response.data, error: null } }))
          } else {
            const error = new Error(`Run ${slug} answered ${String(response.status)}.`)
            setAnswers((prev) => ({ ...prev, [slug]: { fired: null, error } }))
          }
        },
        onError: (error) => {
          setAnswers((prev) => ({ ...prev, [slug]: { fired: null, error } }))
        },
        onSettled: () => {
          setPendingSlug(null)
        },
      },
    )
  }

  const saveThreshold = (slug: string, threshold: number): void => {
    setSavingSlug(slug)
    setAnswers((prev) => ({ ...prev, [slug]: NO_ANSWER }))
    thresholdMutation.mutate(
      { slug, data: { threshold } },
      {
        onSuccess: (response) => {
          if (response.status !== 200) {
            const error = new Error(`Saving the ${slug} threshold answered ${String(response.status)}.`)
            setAnswers((prev) => ({ ...prev, [slug]: { fired: null, error } }))
          }
        },
        onError: (error) => {
          setAnswers((prev) => ({ ...prev, [slug]: { fired: null, error } }))
        },
        onSettled: () => {
          setSavingSlug(null)
        },
      },
    )
  }

  return (
    <>
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[15rem]" />
          <col className="w-14" />
          <col />
          <col className="w-32" />
          <col className="w-28" />
          <col className="w-24" />
        </colgroup>
        <thead>
          <tr className="border-b border-border">
            <th className={cn(CELL, EYEBROW, 'text-left')}>Stage</th>
            <th className={cn(CELL, EYEBROW, 'text-left')}>State</th>
            <th className={cn(CELL, EYEBROW, 'text-left')}>For this team</th>
            <th className={cn(CELL, EYEBROW, 'text-left')}>Last run</th>
            <th className={cn(CELL, EYEBROW, 'text-left')}>Start at</th>
            <th className={cn(CELL, EYEBROW, 'text-right')} aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {TRAIN_LOOP_NODES.map((node, index) => (
            <StageRows
              key={node.key}
              index={index + 1}
              node={node}
              stage={status[node.key]}
              links={links.filter((link) => link.stage === node.key)}
              jobNames={jobNames}
              pendingSlug={pendingSlug}
              savingSlug={savingSlug}
              answers={answers}
              onRun={(link) => {
                if (link.confirmation === null) {
                  run(link.slug)
                  return
                }
                setAsked(link)
                setIsAsking(true)
              }}
              onSaveThreshold={saveThreshold}
            />
          ))}
        </tbody>
      </table>
      <ConfirmDialog
        open={isAsking}
        onOpenChange={setIsAsking}
        title={asked === null ? '' : `Run ${asked.display_name} now?`}
        description={asked === null ? null : asked.confirmation}
        confirmLabel="Run"
        pendingLabel="Starting..."
        isPending={false}
        error={null}
        onConfirm={() => {
          if (asked !== null) run(asked.slug)
          setIsAsking(false)
        }}
      />
    </>
  )
}

/** The link, or null when the sidebar page it depends on is hidden for the team. */
function useVisibleLink(link: TrainLoopLink | undefined): TrainLoopLink | null {
  const { hiddenNavItems, navConfigLoaded } = useTeam()
  if (link === undefined) return null
  if (link.navRoute === undefined) return link
  const visible = isNavRouteVisible({ to: link.navRoute, hiddenNavItems, navConfigLoaded })
  return visible ? link : null
}

function StageRows({
  index,
  node,
  stage,
  links,
  jobNames,
  pendingSlug,
  savingSlug,
  answers,
  onRun,
  onSaveThreshold,
}: {
  index: number
  node: TrainLoopNode
  stage: TrainLoopStage
  links: PipelineLink[]
  jobNames: ReadonlyMap<string, string>
  pendingSlug: string | null
  savingSlug: string | null
  answers: Record<string, RowAnswer>
  onRun: (link: PipelineLink) => void
  onSaveThreshold: (slug: string, threshold: number) => void
}): React.JSX.Element {
  const link = useVisibleLink(node.link)
  const on = stage.status === 'on'
  return (
    <>
      <tr className="border-t border-border bg-muted/40">
        <td className={cn(CELL, 'py-3 text-sm')}>
          <span className={`${EYEBROW} mr-2 tabular-nums`}>{String(index).padStart(2, '0')}</span>
          <span className="font-medium text-foreground" title={node.summary}>
            {node.label}
          </span>
          {node.gate && <span className="ml-2 text-xs text-muted-foreground">Human gate</span>}
        </td>
        <td className={cn(CELL, 'whitespace-nowrap py-3 text-sm')}>
          <span
            className={cn(
              'mr-1.5 inline-block size-2 rounded-full align-middle',
              on ? 'bg-success' : 'bg-muted-foreground/40',
            )}
          />
          <span className={on ? 'text-foreground' : 'text-muted-foreground'}>
            {STATUS_LABELS[stage.status]}
          </span>
        </td>
        <td className={cn(CELL, 'py-3 text-sm text-foreground')} colSpan={3}>
          {stage.detail}
        </td>
        <td className={cn(CELL, 'py-3 text-right')}>
          {link !== null && (
            <Link
              to={link.to}
              className="text-xs text-foreground underline underline-offset-4 hover:text-primary"
            >
              {link.label}
            </Link>
          )}
        </td>
      </tr>
      {links.map((job) => (
        <JobRow
          key={job.slug}
          link={job}
          jobNames={jobNames}
          stageDetail={stage.detail}
          pending={pendingSlug === job.slug}
          saving={savingSlug === job.slug}
          answer={answers[job.slug] ?? NO_ANSWER}
          onRun={() => {
            onRun(job)
          }}
          onSaveThreshold={(threshold) => {
            onSaveThreshold(job.slug, threshold)
          }}
        />
      ))}
    </>
  )
}

function JobRow({
  link,
  jobNames,
  stageDetail,
  pending,
  saving,
  answer,
  onRun,
  onSaveThreshold,
}: {
  link: PipelineLink
  jobNames: ReadonlyMap<string, string>
  /** The stage's own detail; a reason that repeats it is not shown twice. */
  stageDetail: string
  pending: boolean
  saving: boolean
  answer: RowAnswer
  onRun: () => void
  onSaveThreshold: (threshold: number) => void
}): React.JSX.Element {
  const { isEmployee } = useTeam()
  const state = linkState(link)
  const canRun = state === 'ready' && !pending
  const errorDetail =
    answer.error === null
      ? null
      : (getApiErrorDetail(answer.error) ?? 'The run could not be started.')
  const reason = link.unavailable_reason === stageDetail ? null : link.unavailable_reason
  const { threshold } = link.progress
  return (
    <tr>
      <td className={cn(CELL, 'pl-10 text-sm')}>
        <span
          className="text-foreground"
          title={`${link.condition} Starts after ${describeStartsAfter(link.after, jobNames)}.`}
        >
          {link.display_name}
        </span>
      </td>
      <td className={CELL} />
      <td className={cn(CELL, 'space-y-1 text-xs')}>
        {state === 'off' ? (
          <p className="text-muted-foreground">{reason ?? 'Off for this team.'}</p>
        ) : (
          <p className="text-muted-foreground">{progressText(link.progress)}</p>
        )}
        {answer.fired !== null && (
          <p className="text-foreground" role="status">
            {firedText(answer.fired)}
          </p>
        )}
        {errorDetail !== null && (
          <p className="text-destructive" role="alert">
            {errorDetail}
          </p>
        )}
      </td>
      <td className={cn(CELL, 'whitespace-nowrap text-xs text-muted-foreground')}>
        {state === 'running' ? (
          <span className="inline-flex items-center gap-1.5 text-foreground">
            <Loader2 className="size-3 animate-spin motion-reduce:animate-none" aria-hidden />
            Running
          </span>
        ) : link.last_run === null ? (
          'Never ran'
        ) : (
          lastRunText(link.last_run)
        )}
      </td>
      <td className={cn(CELL, 'py-1.5')}>
        {state !== 'off' && isEmployee && (
          <ThresholdField
            key={threshold}
            slug={link.slug}
            name={link.display_name}
            progress={link.progress}
            saving={saving}
            onSave={onSaveThreshold}
          />
        )}
        {state !== 'off' && !isEmployee && (
          <span className="font-mono text-xs tabular-nums text-muted-foreground">{String(threshold)}</span>
        )}
      </td>
      <td className={cn(CELL, 'py-1.5 text-right')}>
        {isEmployee && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-muted-foreground"
            disabled={!canRun}
            aria-label={`Run ${link.display_name} now`}
            onClick={onRun}
          >
            {pending ? 'Starting...' : 'Run now'}
          </Button>
        )}
      </td>
    </tr>
  )
}

/** The threshold typed in place; Save appears once it differs from the saved one. */
function ThresholdField({
  slug,
  name,
  progress,
  saving,
  onSave,
}: {
  slug: string
  name: string
  progress: TrainLoopLinkProgress
  saving: boolean
  onSave: (threshold: number) => void
}): React.JSX.Element {
  const [text, setText] = useState(String(progress.threshold))
  const error = thresholdError(text)
  const changed = error === null && Number(text) !== progress.threshold
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Input
        id={`${slug}-threshold`}
        className="h-7 w-16 px-1.5 font-mono text-xs tabular-nums"
        type="number"
        min={THRESHOLD_MIN}
        max={THRESHOLD_MAX}
        step={1}
        value={text}
        aria-label={`${name} threshold`}
        aria-invalid={error !== null}
        onChange={(e) => {
          setText(e.target.value)
        }}
      />
      {changed && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2"
          disabled={saving}
          onClick={() => {
            onSave(Number(text))
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      )}
      {error !== null && (
        <p className="w-full text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
